import React from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  DialogTitle,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useFormContext } from 'react-hook-form';

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  icnWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
});

const CustomDialog = function CustomDialog({
  open = false,
  escapeToClose = false,
  fullWidth = false,
  maxWidth = 'md',
  styles = null,
  transitionDuration = 300,
  icon = null,
  title = null,
  showOnly = false,
  components = null,
  onSubmit = () => {},
  disableActions = false,
  notRequiredFields = false,
  enctype = null,
  close = () => {},
  messageDialog = false,
  fullScreen = false,
  id = 'CustomDialog',
  submitDisabled = false,
  noBackdrop = false,
  transition,
  children,
  noForm = false,
}) {
  const theme = useTheme();
  const intl = useIntl();
  const classes = useStyles();
  const methods = useFormContext();

  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { onClose: close });
    }
    return child;
  });

  // eslint-disable-next-line no-unused-vars
  const onError = (err, e) => {
    console.log(err);
  };

  return (
    <Dialog
      id={id}
      fullScreen={fullScreen}
      open={Boolean(open)}
      // MUI 9 has no disableEscapeKeyDown: Esc reaches onClose with its reason, like the backdrop.
      onClose={(event, reason) => {
        if (reason === 'escapeKeyDown' && !escapeToClose) return;
        if (!noBackdrop) close(event, reason);
        else if (reason !== 'backdropClick') close();
      }}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      sx={{ ...styles?.root }}
      slots={{ transition }}
      transitionDuration={transitionDuration}
    >
      <div className="dialog-appbar">
        {components?.toolbar || (
          <DialogTitle
            style={{
              backgroundColor: theme.palette.primary.main,
              color: '#fff',
              ...styles?.title,
            }}
          >
            <div className={classes.toolbar}>
              <div className={classes.icnWrap}>
                {icon}
                {title}
              </div>
              <IconButton onClick={close} color="inherit">
                <CloseIcon />
              </IconButton>
            </div>
          </DialogTitle>
        )}
      </div>

      {showOnly ? (
        <>
          <DialogContent style={{ display: 'flex', ...styles?.content }}>
            {childrenWithProps}
          </DialogContent>
          {!disableActions && (
            <DialogActions>
              <Button
                id="close-dialog-btn"
                onClick={close}
                variant="outlined"
                size="large"
              >
                {intl.messages.CLOSE}
              </Button>
            </DialogActions>
          )}
        </>
      ) : noForm ? (
        <>
          <DialogContent style={{ display: 'flex', ...styles?.content }}>
            {childrenWithProps}
          </DialogContent>
          <DialogActions>
            {components?.actions || (
              <div style={{ display: !disableActions ? 'block' : 'none' }}>
                <Button
                  id="close-dialog-btn"
                  onClick={close}
                  variant="outlined"
                  size="large"
                  sx={{ mr: '8px', minWidth: '130px' }}
                >
                  {intl.messages.CLOSE}
                </Button>
                <Button
                  id="confirm-btn"
                  size="large"
                  sx={{ minWidth: '130px' }}
                  disabled={submitDisabled}
                  onClick={onSubmit}
                >
                  {intl.messages.CONFIRM}
                </Button>
              </div>
            )}
          </DialogActions>
        </>
      ) : (
        <form
          style={{
            display: 'contents', //contents does not creates form element in DOM
          }}
          onSubmit={methods?.handleSubmit(onSubmit, onError)}
          encType={enctype}
          noValidate //to prevent html5 validation (the only validation will be the useForm's)
        >
          <DialogContent style={{ display: 'flex', ...styles?.content }}>
            {childrenWithProps}
          </DialogContent>
          <DialogActions>
            {components?.actions || (
              <div style={{ display: !disableActions ? 'block' : 'none' }}>
                <Button
                  id="close-dialog-btn"
                  onClick={close}
                  variant="outlined"
                  size="large"
                  sx={{ mr: '8px', minWidth: '130px' }}
                >
                  {intl.messages.CLOSE}
                </Button>
                <Button
                  id="submit-btn"
                  type="submit"
                  size="large"
                  sx={{ minWidth: '130px' }}
                  disabled={submitDisabled}
                  onClick={() => {
                    if (
                      methods?.isSubmittedSuccessful &&
                      !notRequiredFields &&
                      !messageDialog
                    ) {
                      close();
                    }
                    if (notRequiredFields && !messageDialog) {
                      close();
                    }
                  }}
                >
                  {intl.messages.CONFIRM}
                </Button>
              </div>
            )}
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};

export default CustomDialog;
